// Optional proof for a checklist update or completion: photos, a video
// taken on the spot, the Excel / PDF / DWG / Word that documents the
// check. Files upload as soon as they are chosen (to the item's /media
// route, or the public /checklist-validate/:token/media one) and the
// descriptors come back to the caller, who links them to the update it
// posts; that is what puts them in the item's Updates thread.
//
//   <ChecklistMediaPicker upload={files => api...} value={list} onChange={setList} />
//
// `upload` takes an array of File and resolves to the server's `added`
// descriptors ({url, name, kind, mime, size}). Paste (Ctrl+V) of a file
// while `pasteActive` is true goes through the same upload. The camera
// button opens the issue page's recorder (photo or video); it is hidden
// where the browser has no camera API.
import React, { useEffect, useState } from "react";
import { Button, Space, Tag, Tooltip, Upload, message } from "antd";
import { PaperClipOutlined, VideoCameraOutlined, FileExcelOutlined, FilePdfOutlined, FileImageOutlined, FileOutlined } from "@ant-design/icons";
import { url } from "api";
import { MEDIA_ACCEPT, MEDIA_ALLOWED_TEXT, mediaKindOf } from "components/Issue/IssueSourceControls";
import IssueCameraCapture from "pages/Issue/components/IssueCameraCapture";

const ICON = { image: FileImageOutlined, video: VideoCameraOutlined, pdf: FilePdfOutlined, excel: FileExcelOutlined };
const COLOR = { image: "#2f6fed", video: "#7b3fe4", pdf: "#d64545", excel: "#1f8f4e", drawing: "#b45309", doc: "#475467" };
const abs = (u) => (/^(https?:|data:|blob:)/i.test(u || "") ? u : `${url}${u}`);

const dataUrlToFile = async (dataUrl, name) => {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], name, { type: blob.type || "image/png" });
};

export function ChecklistMediaChips({ items = [], onRemove, size = "small" }) {
  if (!items.length) return null;
  return (
    <Space size={4} wrap>
      {items.map((a, i) => {
        const k = a.kind || mediaKindOf({ type: a.mime, name: a.name || a.url }) || "file";
        const Icon = ICON[k] || FileOutlined;
        return (
          <Tag key={`${a.url}-${i}`} closable={!!onRemove} onClose={(e) => { e.preventDefault(); onRemove && onRemove(i); }}
               style={{ display: "inline-flex", alignItems: "center", gap: 4, margin: 0, fontSize: size === "small" ? 11.5 : 12.5 }}>
            {k === "image" ? <img src={abs(a.url)} alt="" style={{ height: 18, borderRadius: 2 }} /> : <Icon style={{ color: COLOR[k] || "#667085" }} />}
            <a href={abs(a.url)} target="_blank" rel="noreferrer" style={{ color: "inherit", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name || k}</a>
          </Tag>
        );
      })}
    </Space>
  );
}

// The files of a posted update, shown in the thread: images and video
// inline, documents as links.
export function ChecklistThreadMedia({ attachments = [], screenshotUrl }) {
  const list = attachments.length ? attachments : (screenshotUrl ? [{ url: screenshotUrl, kind: "image", name: "screenshot" }] : []);
  if (!list.length) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
      {list.map((a, i) => {
        const k = a.kind || mediaKindOf({ type: a.mime, name: a.name || a.url }) || "file";
        if (k === "image") return <a key={i} href={abs(a.url)} target="_blank" rel="noreferrer"><img src={abs(a.url)} alt={a.name || "image"} style={{ maxWidth: 240, maxHeight: 160, borderRadius: 4, display: "block" }} /></a>;
        if (k === "video") return <video key={i} src={abs(a.url)} controls preload="metadata" style={{ maxWidth: 280, maxHeight: 180, borderRadius: 4, background: "#000" }} />;
        const Icon = ICON[k] || FileOutlined;
        return <a key={i} href={abs(a.url)} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid #eee", borderRadius: 4, padding: "2px 8px", fontSize: 12 }}><Icon style={{ color: COLOR[k] || "#667085" }} />{a.name || k}</a>;
      })}
    </div>
  );
}

export default function ChecklistMediaPicker({ upload, value = [], onChange, pasteActive = false, hint, block = false, camera = true }) {
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const hasCamera = camera && typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  const take = async (files) => {
    const arr = [...(files || [])].filter(Boolean);
    if (!arr.length) return;
    const bad = arr.filter((f) => !mediaKindOf(f));
    if (bad.length) message.warning(`${bad.length} file${bad.length > 1 ? "s" : ""} skipped (${MEDIA_ALLOWED_TEXT} only)`);
    const ok = arr.filter((f) => mediaKindOf(f));
    if (!ok.length) return;
    setBusy(true);
    try {
      const added = await upload(ok);
      if (added && added.length) {
        onChange([...(value || []), ...added]);
        message.success(`${added.length} file${added.length > 1 ? "s" : ""} attached - they go out with your update`);
      }
    } catch (e) { message.error(e.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!pasteActive) return undefined;
    const onPaste = (e) => {
      const tag = document.activeElement?.tagName;
      const dt = e.clipboardData;
      const files = dt?.files && dt.files.length ? [...dt.files]
        : [...(dt?.items || [])].filter((it) => it.kind === "file").map((it) => it.getAsFile()).filter(Boolean);
      if (!files.length) return;
      if (["INPUT", "TEXTAREA"].includes(tag) && !files.length) return;
      e.preventDefault();
      take(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteActive, value]);

  return (
    <div style={block ? { display: "flex", flexDirection: "column", gap: 6 } : undefined}>
      <Space wrap>
        <Upload accept={MEDIA_ACCEPT} multiple showUploadList={false}
                beforeUpload={(file, fileList) => { if (fileList && fileList[0] === file) take(fileList); return false; }}>
          <Button size="small" icon={<PaperClipOutlined />} loading={busy} block={block}>Attach files</Button>
        </Upload>
        {hasCamera && (
          <Tooltip title="Take a photo or record a video with this device's camera">
            <Button size="small" icon={<VideoCameraOutlined />} onClick={() => setCameraOpen(true)}>Camera</Button>
          </Tooltip>
        )}
        <span style={{ fontSize: 11, color: "#999" }}>{hint || `optional - images, video, PDF, Excel, DWG, Word${pasteActive ? " · or paste (Ctrl/Cmd+V)" : ""}`}</span>
      </Space>
      <ChecklistMediaChips items={value} onRemove={(i) => onChange((value || []).filter((_, j) => j !== i))} />
      {hasCamera && cameraOpen && (
        <IssueCameraCapture
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onPhoto={async (dataUrl) => { await take([await dataUrlToFile(dataUrl, `photo-${Date.now()}.png`)]); }}
          onVideo={async (file) => { await take([file]); }}
        />
      )}
    </div>
  );
}
